/**
 * Share Book Event Log Component
 *
 * Displays the audit trail (event log) for ABL compliance.
 */

import { useState } from "react";
import {
  Shield,
  CheckCircle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useShareBookEvents } from "@/hooks/use-share-book";

interface ShareBookEventLogProps {
  companyId: string;
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  // Aktiebok
  "share_book.created": "Aktiebok skapad",
  "share_book.updated": "Aktiebok uppdaterad",
  "share_book.locked": "Aktiebok låst",
  "share_book.unlocked": "Aktiebok upplåst",
  // Aktieslag
  "share_class.created": "Aktieslag skapat",
  "share_class.updated": "Aktieslag uppdaterat",
  "share_class.deleted": "Aktieslag borttaget",
  // Aktieägare
  "shareholder.created": "Aktieägare skapad",
  "shareholder.updated": "Aktieägare uppdaterad",
  "shareholder.deactivated": "Aktieägare avaktiverad",
  // Transaktioner
  "transaction.created": "Transaktion skapad",
  "transaction.corrected": "Transaktion korrigerad",
  "transaction.verified": "Transaktion verifierad",
  "transaction.founding": "Bildande registrerat",
  "transaction.purchase": "Köp registrerat",
  "transaction.gift": "Gåva registrerad",
  "transaction.inheritance": "Arv registrerat",
  "transaction.new_issue": "Nyemission registrerad",
  // Innehav
  "holding.created": "Innehav skapat",
  "holding.invalidated": "Innehav ogiltigförklarat",
  // Pantsättning
  "pledge.added": "Pantsättning tillagd",
  "pledge.removed": "Pantsättning borttagen",
  // Kapitalförändringar
  "capital_change.created": "Kapitalförändring registrerad",
  // Klausuler
  "clause.updated": "Klausul uppdaterad",
  // Export
  "pdf.exported": "PDF exporterad",
};

export function ShareBookEventLog({ companyId }: ShareBookEventLogProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: events = [], isLoading } = useShareBookEvents(companyId, {
    limit: 100,
  });

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getEventLabel = (eventType: string): string => {
    return EVENT_TYPE_LABELS[eventType] || eventType;
  };

  const getEventCategory = (eventType: string): string => {
    const [category] = eventType.split(".");
    switch (category) {
      case "share_book":
        return "Aktiebok";
      case "share_class":
        return "Aktieslag";
      case "shareholder":
        return "Aktieägare";
      case "transaction":
        return "Transaktion";
      case "holding":
        return "Innehav";
      case "pledge":
        return "Pantsättning";
      case "capital_change":
        return "Kapital";
      case "clause":
        return "Klausul";
      case "pdf":
        return "Export";
      default:
        return category;
    }
  };

  const getCategoryColor = (eventType: string): string => {
    const [category] = eventType.split(".");
    switch (category) {
      case "share_book":
        return "bg-blue-100 text-blue-700";
      case "share_class":
        return "bg-purple-100 text-purple-700";
      case "shareholder":
        return "bg-green-100 text-green-700";
      case "transaction":
        return "bg-amber-100 text-amber-700";
      case "holding":
        return "bg-pink-100 text-pink-700";
      case "pledge":
        return "bg-red-100 text-red-700";
      case "capital_change":
        return "bg-indigo-100 text-indigo-700";
      case "clause":
        return "bg-cyan-100 text-cyan-700";
      case "pdf":
        return "bg-emerald-100 text-emerald-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded w-1/3" />
          <div className="h-16 bg-gray-100 rounded" />
          <div className="h-16 bg-gray-100 rounded" />
          <div className="h-16 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Shield className="text-gray-400" size={24} />
        <div>
          <h2 className="text-lg font-bold text-gray-900">Händelselogg</h2>
          <p className="text-sm text-gray-500">
            Komplett spårbarhet enligt ABL 5 kap (immutable audit trail)
          </p>
        </div>
      </div>

      {/* Events List */}
      {events.length > 0 ? (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />

          <div className="space-y-4">
            {events.map((event) => {
              const isExpanded = expandedId === event.id;

              return (
                <div key={event.id} className="relative pl-10">
                  {/* Timeline dot */}
                  <div
                    className={`absolute left-2.5 w-3 h-3 rounded-full ${
                      event.checksum ? "bg-green-500" : "bg-gray-300"
                    }`}
                  />

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Event Header */}
                    <button
                      onClick={() => toggleExpand(event.id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-center min-w-[60px]">
                          <span className="text-xs text-gray-500">
                            {new Date(event.createdAt).toLocaleDateString("sv-SE", {
                              day: "numeric",
                              month: "short",
                            })}
                          </span>
                          <br />
                          <span className="text-xs text-gray-400">
                            {new Date(event.createdAt).toLocaleTimeString("sv-SE", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(event.eventType)}`}>
                              {getEventCategory(event.eventType)}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-900 mt-1">
                            {getEventLabel(event.eventType)}
                          </p>
                          {event.userName && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              av {event.userName}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {event.checksum && (
                          <span title="Verifierad checksumma">
                            <CheckCircle size={16} className="text-green-500" />
                          </span>
                        )}
                        {isExpanded ? (
                          <ChevronDown className="text-gray-400" size={20} />
                        ) : (
                          <ChevronRight className="text-gray-400" size={20} />
                        )}
                      </div>
                    </button>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                        <div className="mt-4 space-y-4">
                          {/* Event Data */}
                          <div>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                              Händelsedata
                            </h4>
                            <pre className="p-3 bg-gray-50 rounded text-xs overflow-x-auto">
                              {JSON.stringify(event.eventData, null, 2)}
                            </pre>
                          </div>

                          {/* Previous Data (if exists) */}
                          {event.previousData && (
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                Föregående data
                              </h4>
                              <pre className="p-3 bg-gray-50 rounded text-xs overflow-x-auto">
                                {JSON.stringify(event.previousData, null, 2)}
                              </pre>
                            </div>
                          )}

                          {/* Metadata */}
                          <div className="grid sm:grid-cols-2 gap-4 text-sm">
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Händelse-ID:</span>
                                <span className="text-gray-900 font-mono text-xs">{event.id}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Entitetstyp:</span>
                                <span className="text-gray-900">{event.entityType}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Entitets-ID:</span>
                                <span className="text-gray-900 font-mono text-xs">{event.entityId}</span>
                              </div>
                            </div>
                            <div className="space-y-1">
                              {event.userEmail && (
                                <div className="flex justify-between">
                                  <span className="text-gray-500">E-post:</span>
                                  <span className="text-gray-900">{event.userEmail}</span>
                                </div>
                              )}
                              {event.ipAddress && (
                                <div className="flex justify-between">
                                  <span className="text-gray-500">IP-adress:</span>
                                  <span className="text-gray-900 font-mono text-xs">{event.ipAddress}</span>
                                </div>
                              )}
                              {event.checksum && (
                                <div className="flex justify-between">
                                  <span className="text-gray-500">SHA-256:</span>
                                  <span className="text-gray-900 font-mono text-xs truncate max-w-[180px]" title={event.checksum}>
                                    {event.checksum}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <Shield className="mx-auto text-gray-300 mb-3" size={48} />
          <p className="text-gray-600">
            Ingen aktivitet registrerad ännu
          </p>
        </div>
      )}
    </div>
  );
}
